import { CREATE_OR_UPDATE_PERMISOS_SI } from "@/apollo/mutations/epp";
import { GET_PERMISOS_SI, GET_PERMISO_SI_EMPRESA } from "@/apollo/queries/epp";
import { exMut, exQue } from "@/utils/gql";
import Vue from "vue";

// Permisos de EPP de la empresa (tablas si_permisos y si_permisos_empresas), que
// se editan en Dashboard/ModalConfiguracionEpp.vue. En el modal son dos switches:
//   colaborador_solicita: permiso con colaborador (on) o con solo_jefe (off)
//   automatizaciones: permiso con automatizaciones true (on) o false (off)
// Los permisos se buscan en el catálogo por esas columnas, no por id.
//
// Lo guardado se deja también en el estado (configuracionEpp): con automatizaciones
// en off la pestaña Automatización no existe (getter automatizacionesActivas).
//
// Lo que hace el backend con ellos: al hacer login en la app o la intranet calcula
// puede_solicitar (con solo_jefe, solo los jefes). Sin permisos configurados deja
// solicitar a todos, así que ambos switches parten encendidos.

const toast = (message, type) => {
  Vue.$toast.open({ message, type, duration: 5000, position: "top-right" });
};

// Switches a partir de los permisos guardados de la empresa
const configuracionDesdePermisos = (permisosEmpresa = []) => {
  const permisos = permisosEmpresa.map((item) => item?.permiso).filter(Boolean);
  const soloJefe = permisos.some((p) => p.solo_jefe === true);
  const colaborador = permisos.some((p) => p.colaborador === true);
  return {
    colaborador_solicita: colaborador || !soloJefe,
    automatizaciones: !permisos.some((p) => p.automatizaciones === false),
  };
};

// Deja en el estado la configuración de la empresa del usuario. Lleva su
// id_empresa porque el store no se limpia al cerrar sesión: sin él, quien entra con
// otra empresa en la misma pestaña vería la configuración de la anterior
const guardarEnEstado = (commit, rootGetters, configuracion) =>
  commit("SET_CONFIGURACION_EPP", {
    id_empresa: Number(rootGetters["loginModule/idEmpresa"]),
    colaborador_solicita: Boolean(configuracion.colaborador_solicita),
    automatizaciones: Boolean(configuracion.automatizaciones),
  });

export const PermisosState = {
  permisosCatalogo: [],
  // { id_empresa, colaborador_solicita, automatizaciones } de la empresa del
  // usuario; null mientras no se ha leído
  configuracionEpp: null,
};

export const PermisosMutations = {
  SET_PERMISOS_CATALOGO(state, opciones) {
    state.permisosCatalogo = opciones;
  },
  SET_CONFIGURACION_EPP(state, configuracion) {
    state.configuracionEpp = configuracion;
  },
};

export const PermisosGetters = {
  // Si la empresa tiene las automatizaciones activadas. Mientras no se lee (o si
  // la lectura falló, o es de otra empresa) vale true, que es el valor de una
  // empresa sin permisos configurados: no se esconde la pestaña por un error
  automatizacionesActivas(state, getters, rootState, rootGetters) {
    const configuracion = state.configuracionEpp;
    if (!configuracion) return true;
    if (configuracion.id_empresa !== Number(rootGetters["loginModule/idEmpresa"])) {
      return true;
    }
    return configuracion.automatizaciones !== false;
  },
};

export const PermisosActions = {
  // Devuelve { colaborador_solicita, automatizaciones }, o null si no se pudo leer
  async getConfiguracionEpp({ commit, rootGetters }) {
    try {
      const [catalogo, permisosEmpresa] = await Promise.all([
        exQue(GET_PERMISOS_SI, {}, true),
        exQue(GET_PERMISO_SI_EMPRESA, {}, true),
      ]);
      const opciones = (catalogo || []).flatMap((grupo) => grupo?.opciones || []);
      commit("SET_PERMISOS_CATALOGO", opciones);
      const configuracion = configuracionDesdePermisos(permisosEmpresa || []);
      guardarEnEstado(commit, rootGetters, configuracion);
      return configuracion;
    } catch {
      // exQue ya dejó el error en consola
      toast("No se pudo cargar la configuración de EPP", "error");
      return null;
    }
  },

  // Para la pestaña Automatización: lee la configuración de la empresa una sola
  // vez (la guarda también el modal al abrirse y al guardar). Sin toast si falla:
  // queda sin leer y se muestra todo, como antes
  async ensureConfiguracionEpp({ state, commit, rootGetters }) {
    const idEmpresa = Number(rootGetters["loginModule/idEmpresa"]);
    if (state.configuracionEpp?.id_empresa === idEmpresa) return state.configuracionEpp;

    try {
      const permisosEmpresa = await exQue(GET_PERMISO_SI_EMPRESA, {}, true);
      guardarEnEstado(commit, rootGetters, configuracionDesdePermisos(permisosEmpresa || []));
    } catch {
      // exQue ya dejó el error en consola
    }
    return state.configuracionEpp;
  },

  // Devuelve true si quedó guardada
  async guardarConfiguracionEpp({ state, commit, rootGetters }, configuracion) {
    const opciones = state.permisosCatalogo;
    const solicitud = opciones.find((p) =>
      configuracion.colaborador_solicita ? p.colaborador === true : p.solo_jefe === true,
    );
    const automatizacion = opciones.find(
      (p) => p.automatizaciones === Boolean(configuracion.automatizaciones),
    );
    if (!solicitud || !automatizacion) {
      // Pasa si el catálogo del backend está vacío (seeder sin correr)
      toast("No hay opciones de configuración disponibles", "error");
      return false;
    }

    // El backend no usa transacción: si falla una inserción responde igual con
    // las que sí quedaron. Se compara lo guardado con lo pedido.
    const ids = [Number(solicitud.id_permiso), Number(automatizacion.id_permiso)];
    const guardados = await exMut(
      CREATE_OR_UPDATE_PERMISOS_SI,
      { id_permiso: ids },
      "",
      "No se pudo guardar la configuración de EPP",
    );
    if (!guardados) return false;

    const idsGuardados = guardados.map((item) => Number(item?.permiso?.id_permiso));
    if (!ids.every((id) => idsGuardados.includes(id))) {
      toast("La configuración de EPP no se guardó completa. Vuelve a intentarlo.", "error");
      return false;
    }

    guardarEnEstado(commit, rootGetters, configuracion);
    toast("Configuración de EPP guardada correctamente", "success");
    return true;
  },
};
